import { ApolloError } from '@apollo/client';
import { Link, Stack } from '@mui/material';
import { SetOptional } from 'type-fest';

import {
  FormModal,
  FormModalProps,
} from '@/components/modals/FormModal/FormModal';
import { FormModalActions } from '@/components/modals/FormModal/FormModalActions/FormModalActions';
import { Callout } from '@/components/notifications/Callout/Callout';
import {
  FeedbackMessages,
  useFeedback,
} from '@/components/notifications/Feedback/useFeedback';
import { FormProvider, useFormContext } from '@/components/react-hook-form';
import { useForm, useSubmitSuccessHandler } from '@/components/react-hook-form';
import { getUserFacingErrorMessages } from '@/graphql/errors';
import { useFormSaveHandler } from '@/hooks/useFormSaveHandler';
import { useReportError } from '@/hooks/useReportError';
import {
  BehaviorAuthorizationType,
  useHasBehaviorAuthorization,
} from '@/modules/authentication/hooks/useHasBehaviorAuthorization';

import { CollaboratorForm } from './CollaboratorForm';
import {
  CollaboratorFormShape,
  defaultValues,
} from './CollaboratorModal.constants';
import { mapFormDataToInput } from './CreateCollaboratorModal.utils';
import { useCreateCollaboratorMutation } from './graphql/CollaboratorModal.generated';

type FormModalOptionalProps = SetOptional<FormModalProps, 'heading'>;
export type CreateCollaboratorModalProps = FormModalOptionalProps & {
  onOpenInviteColleague: () => void;
};

function CreateCollaborator({
  onClose,
  heading = 'Add external collaborator',
  onOpenInviteColleague,
  ...formModalProps
}: CreateCollaboratorModalProps) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const { formRef, handleSave } = useFormSaveHandler();
  const canViewHouseholds = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_VIEW_HOUSEHOLDS,
    { detailed: false }
  );
  const { handleSubmit, reset, shouldBlockNavigation, formState } =
    useFormContext<CollaboratorFormShape>();

  const [createMutation] = useCreateCollaboratorMutation({
    refetchQueries: 'active',
    awaitRefetchQueries: true,
  });

  const onSubmit = handleSubmit(async (formData) => {
    try {
      await createMutation({
        variables: { input: mapFormDataToInput(formData, !!canViewHouseholds) },
      });
    } catch (error) {
      const userFacingMessages =
        error instanceof ApolloError ? getUserFacingErrorMessages(error) : [];
      showFeedback(
        userFacingMessages.length > 0
          ? userFacingMessages.join('. ')
          : FeedbackMessages.formSaveError
      );
      reportError('Could not create collaborator', error as Error);
      // Re-throw so react-hook-form marks submission as failed
      // and useSubmitSuccessHandler doesn't close the modal
      throw error;
    }
  });

  const closeModal = () => {
    reset();
    onClose();
  };

  useSubmitSuccessHandler(() => {
    closeModal();
  });

  return (
    <FormModal
      {...formModalProps}
      onClose={onClose}
      heading={heading}
      shouldBlockClose={shouldBlockNavigation}
      actions={
        <FormModalActions.Provider formState={formState}>
          <FormModalActions.CancelButton onClick={closeModal} />
          <FormModalActions.SaveButton onClick={handleSave}>
            Add external collaborator
          </FormModalActions.SaveButton>
        </FormModalActions.Provider>
      }
    >
      <Stack spacing={3}>
        <Callout severity="info-high">
          Collaborators are limited to individuals outside of your organization.
          To invite a colleague to Luminary{' '}
          <Link
            component="button"
            type="button"
            onClick={onOpenInviteColleague}
            underline="always"
            variant="inherit"
          >
            click here
          </Link>
          .
        </Callout>
        <form ref={formRef} onSubmit={onSubmit} noValidate>
          <CollaboratorForm variant="create" />
        </form>
      </Stack>
    </FormModal>
  );
}

export const CreateCollaboratorModal = ({
  ...props
}: CreateCollaboratorModalProps) => {
  const formMethods = useForm<CollaboratorFormShape>({ defaultValues });

  return (
    <FormProvider {...formMethods}>
      <CreateCollaborator {...props} />
    </FormProvider>
  );
};
